import {
    KIND_CONTROL_2M,
    KIND_EXTRA,
    KIND_MONTH_1,
    KIND_MONTH_2,
    KIND_WEEK_1,
    ROLE_EMPLOYEE,
    ROLE_HR,
    ROLE_MANAGER,
} from "./rules";

const SCALE = "scale_1_5";
const SCALE_NA = "scale_na";
const SCALE_OBS = "scale_obs";
const TEXT = "text";
const CHOICE = "choice";
const MULTI = "multi";

const NOT_APPLICABLE = { value: "not_applicable", label: "не применимо" };
const INSUFFICIENT_DATA = { value: "insufficient_data", label: "недостаточно данных" };
const INSUFFICIENT_OBSERVATIONS = {
    value: "insufficient_observations",
    label: "недостаточно наблюдений",
};

export const CORE_EMPLOYEE_IDS = ["core_e1", "core_e2", "core_e3", "core_e4", "core_e5"];

const LOAD_OPTS = [
    "слишком низкая",
    "комфортная",
    "высокая, но комфортная",
    "высокая и перегружает",
    "пока сложно оценить",
];

function q(id, text, type, extra = {}) {
    const { required = true, ...rest } = extra;
    return { id, text, type, required, ...rest };
}

function coreEmployee() {
    const cores = [
        ["core_e1", "Насколько комфортно вы чувствуете себя на рабочем месте?", "1 — очень некомфортно; 5 — полностью комфортно"],
        ["core_e2", "Насколько вам понятны текущие задачи, приоритеты и ожидаемые результаты?", "1 — совершенно непонятны; 5 — полностью понятны"],
        ["core_e3", "Насколько уверенно вы чувствуете себя при выполнении текущих задач?", "1 — постоянно требуется помощь; 5 — справляюсь уверенно и самостоятельно"],
        ["core_e4", "Насколько комфортно вам взаимодействовать с коллегами?", "1 — взаимодействовать некомфортно; 5 — взаимодействовать полностью комфортно"],
        ["core_e5", "Насколько вам хватает поддержки для выполнения работы?", "1 — поддержки не хватает; 5 — поддержки полностью хватает"],
    ];
    const out = [];
    cores.forEach(([id, text, hint]) => {
        out.push(q(id, text, SCALE, { hint, core: true }));
        out.push(
            q(
                `${id}_explain`,
                "Что именно сейчас вызывает сложности по этому показателю? Если оценка заметно изменилась относительно прошлого этапа — что на это повлияло?",
                TEXT,
                {
                    required: true,
                    show_if: { any: [{ field: id, lte: 2 }, { field: id, delta_gte: 2 }] },
                }
            )
        );
    });
    return out;
}

function coreManager() {
    const cores = [
        ["core_m1", "Насколько сотруднику понятны текущие задачи, приоритеты и ожидаемые результаты?", "1 — регулярно требуется повторное разъяснение; 5 — полностью понимает и корректно действует"],
        ["core_m2", "Насколько стабильно сотрудник выполняет текущие задачи с требуемым качеством и в согласованные сроки?", "1 — регулярно возникают проблемы; 5 — стабильно выполняет качественно и в срок"],
        ["core_m3", "Насколько самостоятельно сотрудник работает на текущем этапе?", "1 — требуется постоянный пошаговый контроль; 5 — работает самостоятельно в пределах роли"],
        ["core_m4", "Насколько эффективно сотрудник взаимодействует с коллегами?", "1 — взаимодействие мешает работе; 5 — взаимодействие помогает достигать результата"],
        ["core_m5", "Насколько конструктивно сотрудник воспринимает и применяет обратную связь?", "1 — не принимает или не применяет; 5 — учитывает и заметно улучшает работу"],
        ["core_m6", "Насколько сотрудник справляется с текущим объемом и сложностью задач?", "1 — явно не справляется; 5 — справляется уверенно и стабильно"],
    ];
    const out = [];
    cores.forEach(([id, text, hint]) => {
        out.push(q(id, text, SCALE_OBS, {
            hint,
            core: true,
            special_options: [INSUFFICIENT_OBSERVATIONS],
        }));
        out.push(
            q(`${id}_explain`, "На каких фактах основана эта оценка и что необходимо изменить? Если оценка заметно изменилась — что на это повлияло?", TEXT, {
                show_if: { any: [{ field: id, lte: 2 }, { field: id, delta_gte: 2 }] },
            })
        );
    });
    return out;
}

function talk(qid = "talk_hr", stageLabel = "") {
    const suffix = stageLabel ? ` по итогам ${stageLabel}` : "";
    return [
        q(qid, `Хотели бы вы конфиденциально поговорить с HR${suffix}?`, CHOICE, { options: ["да", "нет"] }),
        q(`${qid}_topic`, "Кратко укажите тему разговора, если вам комфортно ее обозначить.", TEXT, {
            required: false,
            show_if: { field: qid, eq: "да" },
        }),
    ];
}

const WEEK_REST = [
    q("w_access", "Получили ли вы необходимые доступы, материалы, оборудование и инструкции для работы?", CHOICE, {
        options: ["да, полностью", "частично", "нет", "не применимо"],
    }),
    q("w_access_note", "Укажите, чего именно не хватает.", TEXT, { show_if: { field: "w_access", in: ["частично", "нет"] } }),
    q("w_who", "Понимаете ли вы, к кому обращаться по разным рабочим вопросам?", CHOICE, {
        options: ["полностью понимаю", "в основном понимаю", "понимаю частично", "не понимаю"],
    }),
    q("w_who_note", "По каким вопросам пока непонятно, к кому обращаться?", TEXT, {
        show_if: { field: "w_who", in: ["понимаю частично", "не понимаю"] },
    }),
    q("w_intro", "Насколько понятным и последовательным было введение в должность в первую неделю?", SCALE, {
        hint: "1 — было хаотично и непонятно; 5 — все было понятно и последовательно",
    }),
    q("w_feedback", "Получили ли вы обратную связь от руководителя или наставника по первым задачам?", CHOICE, {
        options: ["да, ее достаточно", "да, но ее недостаточно", "пока не получил(а)", "обратная связь пока не требовалась"],
    }),
    q("w_feedback_note", "Какой обратной связи вам сейчас не хватает?", TEXT, {
        show_if: { field: "w_feedback", in: ["да, но ее недостаточно", "пока не получил(а)"] },
    }),
    q("w_load", "Как вы оцениваете текущую рабочую нагрузку?", CHOICE, { options: LOAD_OPTS }),
    q("w_hard", "Какие задачи или процессы на первой неделе были наиболее сложными и почему?", TEXT),
    q("w_ok", "Что за первую неделю уже стало получаться?", TEXT),
    q("w_useful", "Что было наиболее полезным в вашем введении в должность?", TEXT),
    q("w_missing", "Чего вам не хватило в первую неделю?", TEXT, { required: false }),
    q("w_conflict", "Возникали ли сложности во взаимодействии с коллегами?", CHOICE, {
        options: ["нет", "были незначительные", "были существенные"],
    }),
    q("w_conflict_note", "Опишите ситуацию и что могло бы помочь ее решить.", TEXT, {
        show_if: { field: "w_conflict", in: ["были незначительные", "были существенные"] },
    }),
    q("w_help", "Какая помощь нужна вам сейчас?", MULTI, {
        options: ["разъяснение задач", "обучение", "доступы или материалы", "помощь руководителя", "помощь коллег", "разговор с HR", "помощь не требуется", "другое"],
        exclusive_options: ["помощь не требуется"],
    }),
    q("w_help_note", "Опишите необходимую помощь подробнее.", TEXT, {
        show_if: { field: "w_help", not_only: ["помощь не требуется"] },
    }),
    ...talk("w_talk", "первой недели"),
    q("w_other", "Есть ли что-то важное, о чем мы не спросили?", TEXT, { required: false }),
];

const MONTH_REST = [
    q("m1_duties", "Насколько вам понятны ваши должностные обязанности и границы ответственности?", SCALE, { hint: "1 — совершенно непонятны; 5 — полностью понятны" }),
    q("m1_kpi", "Насколько вам понятны критерии оценки вашей работы, KPI или ожидаемые результаты?", SCALE_NA, { hint: "1 — совершенно непонятны; 5 — полностью понятны", special_options: [NOT_APPLICABLE] }),
    q("m1_solo", "Что за первый месяц вы уже выполняете самостоятельно?", TEXT),
    q("m1_need_help", "В каких задачах или процессах вам все еще нужна помощь?", TEXT),
    q("m1_results", "Какие результаты первого месяца вы считаете наиболее значимыми?", TEXT),
    q("m1_load", "Как вы оцениваете текущую рабочую нагрузку?", CHOICE, { options: LOAD_OPTS }),
    q("m1_fb", "Получаете ли вы достаточно обратной связи от руководителя?", CHOICE, {
        options: ["да, достаточно", "скорее достаточно", "недостаточно", "почти не получаю", "пока сложно оценить"],
    }),
    q("m1_fb_note", "Какой обратной связи вам не хватает?", TEXT, {
        show_if: { field: "m1_fb", in: ["недостаточно", "почти не получаю"] },
    }),
    q("m1_fb_useful", "Насколько полезна обратная связь, которую вы получаете?", SCALE_NA, { hint: "1 — не помогает улучшать работу; 5 — помогает заметно улучшать работу", special_options: [INSUFFICIENT_DATA] }),
    q("m1_week_issues", "Как изменились основные сложности, выявленные после первой недели?", CHOICE, {
        options: ["полностью решены", "частично решены", "не изменились", "усилились", "на первой неделе значимых сложностей не было"],
    }),
    q("m1_week_issues_note", "Что изменилось, что помогло и что осталось нерешенным?", TEXT, {
        show_if: { field: "m1_week_issues", in: ["частично решены", "не изменились", "усилились"] },
    }),
    q("m1_expect", "Насколько ваши ожидания от работы совпали с реальностью?", SCALE, { hint: "1 — совсем не совпали; 5 — полностью совпали" }),
    q("m1_expect_note", "Что оказалось иначе, чем вы ожидали?", TEXT, { show_if: { field: "m1_expect", lte: 3 } }),
    q("m1_stay", "Насколько вы хотите продолжать работу в компании?", SCALE, { hint: "1 — не хочу продолжать; 5 — определенно хочу продолжать", strong_signal: true }),
    q("m1_stay_note", "Что сильнее всего влияет на вашу оценку?", TEXT, { show_if: { field: "m1_stay", lte: 3 } }),
    q("m1_motivation", "Что сейчас сильнее всего поддерживает вашу мотивацию?", MULTI, {
        options: ["содержание работы", "первые результаты", "команда", "руководитель", "условия работы", "доход", "развитие", "стабильность", "другое", "пока сложно сказать"],
        exclusive_options: ["пока сложно сказать"],
    }),
    q("m1_next_help", "Какая помощь, обучение или ресурсы нужны вам в следующем месяце?", TEXT),
    q("m1_change", "Что стоит изменить в работе или адаптации в течение следующего месяца?", TEXT, { required: false }),
    ...talk("m1_talk", "первого месяца"),
    q("m1_other", "Есть ли что-то важное, о чем мы не спросили?", TEXT, { required: false }),
];

const MONTH2_REST = [
    q("m2_duties", "Насколько вам понятны ваши должностные обязанности и границы ответственности?", SCALE, { hint: "1 — совершенно непонятны; 5 — полностью понятны" }),
    q("m2_kpi", "Насколько вам понятны критерии оценки вашей работы, KPI или ожидаемые результаты?", SCALE_NA, { hint: "1 — совершенно непонятны; 5 — полностью понятны", special_options: [NOT_APPLICABLE] }),
    q("m2_level", "Насколько, по вашему мнению, вы вышли на ожидаемый для этого этапа уровень эффективности?", SCALE, { hint: "1 — пока не вышел(а); 5 — полностью вышел(а)" }),
    q("m2_level_note", "Что пока не позволяет выйти на ожидаемый уровень?", TEXT, { show_if: { field: "m2_level", lte: 3 } }),
    q("m2_results", "Какие результаты за два месяца вы считаете наиболее значимыми?", TEXT),
    q("m2_plan", "Насколько самостоятельно вы можете планировать работу и расставлять приоритеты?", SCALE, { hint: "1 — требуется постоянное направление; 5 — планирую и приоритизирую самостоятельно" }),
    q("m2_bounds", "Насколько вам понятно, какие вопросы решать самостоятельно, а какие передавать руководителю?", SCALE, { hint: "1 — границы непонятны; 5 — границы полностью понятны" }),
    q("m2_quality", "Насколько стабильно вам удается выполнять задачи качественно и в согласованные сроки?", SCALE, { hint: "1 — регулярно возникают проблемы; 5 — стабильно выполняю качественно и в срок" }),
    q("m2_load", "Как вы оцениваете текущую рабочую нагрузку?", CHOICE, { options: LOAD_OPTS }),
    q("m2_hard", "Какие задачи или процессы все еще вызывают сложности и почему?", TEXT),
    q("m2_better", "Что за последний месяц стало получаться заметно лучше?", TEXT),
    q("m2_team", "Насколько эффективно вы взаимодействуете с коллегами и смежными подразделениями?", SCALE_NA, { hint: "1 — взаимодействие серьезно мешает работе; 5 — взаимодействие помогает достигать результата", special_options: [NOT_APPLICABLE] }),
    q("m2_team_note", "Какие вопросы во взаимодействии требуют дополнительной настройки?", TEXT, { show_if: { field: "m2_team", lte: 3 } }),
    q("m2_culture", "Насколько вам подходит корпоративная культура и принятый стиль работы?", SCALE, { hint: "1 — совершенно не подходит; 5 — полностью подходит" }),
    q("m2_stay", "Насколько вы хотите продолжать работу в компании?", SCALE, { hint: "1 — не хочу продолжать; 5 — определенно хочу продолжать", strong_signal: true }),
    q("m2_stay_note", "Что сильнее всего влияет на вашу оценку?", TEXT, { show_if: { field: "m2_stay", lte: 3 } }),
    q("m2_blockers", "Что может помешать вам успешно продолжать работу в компании?", TEXT, { required: false }),
    q("m2_need", "Какая помощь, обучение, информация или ресурсы нужны вам сейчас?", TEXT),
    q("m2_goals", "Какие цели или зоны роста вы видите для себя на ближайшие один-два месяца?", TEXT),
    q("m2_adapt", "Что, на ваш взгляд, стоит улучшить в адаптации новых сотрудников?", TEXT, { required: false }),
    ...talk("m2_talk", "двух месяцев"),
    q("m2_other", "Есть ли что-то важное, о чем мы не спросили?", TEXT, { required: false }),
];

const MANAGER_MONTH = [
    ...coreManager(),
    q("mgr1_duties", "Насколько сотрудник освоил основные обязанности, предусмотренные для первого месяца?", SCALE_OBS, { hint: "1 — практически не освоил; 5 — полностью освоил ожидаемый объем", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr1_kpi", "Насколько сотрудник понимает критерии оценки своей работы, KPI или ожидаемые результаты?", SCALE_NA, { hint: "1 — не понимает; 5 — полностью понимает", special_options: [NOT_APPLICABLE, INSUFFICIENT_OBSERVATIONS] }),
    q("mgr1_pace", "Как вы оцениваете темп обучения и освоения новых задач?", SCALE_OBS, { hint: "1 — существенно ниже ожидаемого; 5 — соответствует или превышает ожидаемый", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr1_rules", "Насколько сотрудник соблюдает договоренности, рабочий график и внутренние правила?", SCALE_OBS, { hint: "1 — регулярные нарушения; 5 — соблюдает стабильно", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr1_results", "Какие результаты сотрудника за первый месяц вы считаете наиболее значимыми?", TEXT),
    q("mgr1_hard", "Какие задачи или процессы пока вызывают у сотрудника наибольшие сложности?", TEXT),
    q("mgr1_strengths", "Какие сильные стороны сотрудника уже проявились в работе?", TEXT),
    q("mgr1_trend", "Как изменилась работа сотрудника по сравнению с первыми неделями?", CHOICE, {
        options: ["заметно улучшилась", "немного улучшилась", "стабильна", "динамика неравномерная", "ухудшилась", "недостаточно наблюдений"],
    }),
    q("mgr1_trend_note", "Что именно повлияло на эту динамику?", TEXT, {
        show_if: { field: "mgr1_trend", not_in: ["стабильна", "недостаточно наблюдений"] },
    }),
    q("mgr1_support", "Какая поддержка уже была предоставлена сотруднику?", MULTI, {
        options: ["разъяснение задач", "обучение", "регулярная обратная связь", "наставник", "корректировка нагрузки", "дополнительные материалы или доступы", "поддержка не требовалась", "другое"],
        exclusive_options: ["поддержка не требовалась"],
    }),
    q("mgr1_next", "Какая поддержка или корректировка нужна сотруднику в следующем месяце?", TEXT),
    q("mgr1_risks", "Какие риски вы видите на текущем этапе?", MULTI, {
        options: ["качество", "сроки", "самостоятельность", "темп обучения", "дисциплина", "взаимодействие", "нагрузка", "мотивация", "риск ухода", "значимых рисков нет", "другое"],
        exclusive_options: ["значимых рисков нет"],
    }),
    q("mgr1_risk_level", "Как вы оцениваете общий уровень риска по адаптации сотрудника?", CHOICE, {
        options: ["значимых рисков нет", "требует внимания", "высокий риск", "недостаточно данных"],
    }),
    q("mgr1_risk_note", "Опишите ключевой риск и факты, на которых основана оценка.", TEXT, {
        show_if: { field: "mgr1_risk_level", in: ["требует внимания", "высокий риск"] },
    }),
    q("mgr1_expect", "Какие результаты и изменения вы ожидаете от сотрудника к концу второго месяца?", TEXT),
    q("mgr1_extra", "Нужна ли дополнительная контрольная точка до окончания испытательного периода?", CHOICE, {
        options: ["да", "нет", "пока не могу определить"],
    }),
    q("mgr1_extra_note", "Укажите рекомендуемый срок и что именно необходимо проверить.", TEXT, {
        show_if: { field: "mgr1_extra", eq: "да" },
    }),
    q("mgr1_comment", "Итоговый комментарий руководителя по первому месяцу.", TEXT, { required: false }),
];

const MANAGER_MONTH2 = [
    ...coreManager(),
    q("mgr2_fit", "Насколько сотрудник соответствует требованиям должности на текущем этапе?", SCALE_OBS, { hint: "1 — не соответствует; 5 — полностью соответствует", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_level", "Насколько сотрудник вышел на ожидаемый уровень эффективности?", SCALE_OBS, { hint: "1 — не вышел; 5 — полностью вышел", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_kpi", "Насколько сотрудник понимает критерии оценки своей работы, KPI или ожидаемые результаты?", SCALE_NA, { hint: "1 — не понимает; 5 — полностью понимает", special_options: [NOT_APPLICABLE, INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_plan", "Насколько самостоятельно сотрудник планирует работу и расставляет приоритеты?", SCALE_OBS, { hint: "1 — требуется постоянное направление; 5 — планирует и приоритизирует самостоятельно", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_bounds", "Насколько корректно сотрудник определяет, какие вопросы решать самостоятельно, а какие передавать руководителю?", SCALE_OBS, { hint: "1 — регулярно ошибается в границах; 5 — корректно действует в пределах ответственности", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_rules", "Насколько сотрудник соблюдает договоренности, рабочий график и внутренние правила?", SCALE_OBS, { hint: "1 — регулярные нарушения; 5 — соблюдает стабильно", special_options: [INSUFFICIENT_OBSERVATIONS] }),
    q("mgr2_results", "Какие результаты сотрудника за два месяца вы считаете наиболее значимыми?", TEXT),
    q("mgr2_strengths", "Какие сильные стороны сотрудника подтвердились устойчиво?", TEXT),
    q("mgr2_gaps", "Какие пробелы, ошибки или зоны развития остаются?", TEXT),
    q("mgr2_trend", "Как вы оцениваете динамику сотрудника за весь период адаптации?", CHOICE, {
        options: ["устойчиво положительная", "положительная, но неравномерная", "без заметных изменений", "отрицательная", "недостаточно данных"],
    }),
    q("mgr2_trend_note", "Что сильнее всего повлияло на итоговую динамику?", TEXT, {
        show_if: { field: "mgr2_trend", not_in: ["недостаточно данных"] },
    }),
    q("mgr2_after", "Какая поддержка потребуется сотруднику после завершения адаптации?", TEXT, { required: false }),
    q("mgr2_risk_level", "Как вы оцениваете общий уровень риска дальнейшей работы сотрудника?", CHOICE, {
        options: ["значимых рисков нет", "риски управляемы", "высокий риск", "недостаточно данных"],
    }),
    q("mgr2_risk_note", "Опишите ключевые риски, факты и условия, при которых они могут быть снижены.", TEXT, {
        show_if: { field: "mgr2_risk_level", in: ["риски управляемы", "высокий риск"] },
    }),
    q("mgr2_keep", "Рекомендуете ли вы продолжить работу сотрудника в текущей должности?", CHOICE, {
        options: ["да, без ограничений", "да, при выполнении рекомендаций", "требуется пересмотр зоны ответственности", "требуется продление оценки, если это допустимо", "нет", "другое"],
    }),
    q("mgr2_keep_note", "Укажите рекомендации, условия или основание решения.", TEXT),
    q("mgr2_ready", "Насколько сотрудник готов к дальнейшей работе без адаптационного сопровождения?", CHOICE, {
        options: ["готов полностью", "в основном готов", "требуется точечное сопровождение", "требуется системное сопровождение", "не готов"],
    }),
    q("mgr2_goals", "Какие цели стоит поставить сотруднику на ближайшие один-два месяца?", TEXT),
    q("mgr2_comment", "Итоговый комментарий руководителя для HR.", TEXT, { required: false }),
];

const HR = [
    q("hr_comment", "Итоговый развернутый комментарий HR.", TEXT, {
        hint: "Итоговый вывод, важный контекст, принятые решения и необходимые действия.",
    }),
];

const EXTRA = [
    ...coreEmployee(),
    q("ex_change", "Как в целом изменилась ваша рабочая ситуация с момента предыдущего адаптационного опроса?", CHOICE, {
        options: ["заметно улучшилась", "немного улучшилась", "не изменилась", "ухудшилась", "это первая дополнительная проверка"],
    }),
    q("ex_change_note", "Что сильнее всего повлияло на изменение ситуации?", TEXT, {
        show_if: { field: "ex_change", in: ["заметно улучшилась", "немного улучшилась", "ухудшилась"] },
    }),
    q("ex_resolved", "Решены ли вопросы или сложности, которые обсуждались ранее?", CHOICE, {
        options: ["полностью решены", "частично решены", "не решены", "появились новые сложности", "ранее значимых сложностей не было"],
    }),
    q("ex_resolved_note", "Что осталось нерешенным или какие новые сложности появились?", TEXT, {
        show_if: { field: "ex_resolved", in: ["частично решены", "не решены", "появились новые сложности"] },
    }),
    q("ex_useful", "Какие действия компании, руководителя или HR были для вас полезны?", TEXT, { required: false }),
    q("ex_help", "Какая помощь нужна вам сейчас?", MULTI, {
        options: ["разъяснение задач", "обратная связь", "обучение", "доступы или материалы", "корректировка нагрузки", "помощь во взаимодействии", "разговор с HR", "помощь не требуется", "другое"],
        exclusive_options: ["помощь не требуется"],
    }),
    q("ex_help_note", "Опишите необходимую помощь подробнее.", TEXT, {
        show_if: { field: "ex_help", not_only: ["помощь не требуется"] },
    }),
    ...talk("ex_talk"),
    q("ex_other", "Есть ли что-то важное, о чем мы не спросили?", TEXT, { required: false }),
];

const CONTROL_HR = [
    q("c2_confirm", "Подтвердите завершение двухмесячного контрольного периода (без анкеты).", CHOICE, {
        options: ["подтверждаю", "требует внимания", "не подтверждаю"],
    }),
    q("hr_comment", "Комментарий HR по контрольному периоду.", TEXT, { required: false }),
];

function cloneQuestion(question) {
    return {
        ...question,
        ...(Array.isArray(question.options) ? { options: [...question.options] } : {}),
        ...(Array.isArray(question.exclusive_options) ? { exclusive_options: [...question.exclusive_options] } : {}),
        ...(Array.isArray(question.special_options)
            ? { special_options: question.special_options.map((option) => ({ ...option })) }
            : {}),
        ...(question.show_if ? { show_if: JSON.parse(JSON.stringify(question.show_if)) } : {}),
    };
}

function cloneQuestions(questions) {
    return (questions || []).map(cloneQuestion);
}

export function formFor(kind, role) {
    if (kind === KIND_CONTROL_2M) return role === ROLE_HR ? cloneQuestions(CONTROL_HR) : [];
    if (role === ROLE_HR) {
        if (kind === KIND_EXTRA) {
            return cloneQuestions([
                q("hr_comment", "Итоговый развернутый комментарий HR.", TEXT, {
                    required: false,
                    hint: "Итоговый вывод, важный контекст, принятые решения и необходимые действия.",
                }),
            ]);
        }
        return cloneQuestions(HR);
    }
    if (role === ROLE_MANAGER) {
        if (kind === KIND_MONTH_1) return cloneQuestions(MANAGER_MONTH);
        if (kind === KIND_MONTH_2) return cloneQuestions(MANAGER_MONTH2);
        return [];
    }
    if (kind === KIND_WEEK_1) return cloneQuestions([...coreEmployee(), ...WEEK_REST]);
    if (kind === KIND_MONTH_1) return cloneQuestions([...coreEmployee(), ...MONTH_REST]);
    if (kind === KIND_MONTH_2) return cloneQuestions([...coreEmployee(), ...MONTH2_REST]);
    if (kind === KIND_EXTRA) return cloneQuestions(EXTRA);
    return cloneQuestions(coreEmployee());
}

/**
 * Keep the approved catalog authoritative even when an older backend returns a
 * stale or accidentally polluted list of options. The server remains the
 * authority for the form composition: normalization never adds omitted
 * questions. Unknown ids are preserved for configurable modules, while known
 * questions get their text, type, conditions and options from the catalog.
 */
export function normalizeQuestionnaire(kind, role, serverQuestions) {
    const approved = formFor(kind, role);
    if (!Array.isArray(serverQuestions)) return approved;
    const incoming = serverQuestions;
    const approvedById = new Map(approved.map((question) => [question.id, question]));
    return incoming
        .filter((question) => question && typeof question.id === "string")
        .map((question) => {
            const canonical = approvedById.get(question.id);
            return canonical
                ? { ...question, ...cloneQuestion(canonical) }
                : cloneQuestion(question);
        });
}

export function formPublicTitle(kind, role) {
    if (role === ROLE_EMPLOYEE && kind === KIND_EXTRA) return "Адаптационный опрос";
    return (
        {
            [KIND_WEEK_1]: "1 неделя",
            [KIND_MONTH_1]: "1 месяц",
            [KIND_MONTH_2]: "2 месяца",
            [KIND_CONTROL_2M]: "Контроль испытательного периода",
            [KIND_EXTRA]: "Дополнительная контрольная точка",
        }[kind] || kind
    );
}
